
import { useEffect } from "react";

import { 
    ActionStatusHelper, 
    FeedBackType, 
    LONG_PROCESS_TOAST_DURATION, 
    MessageHelper, 
    ResponseHook, 
    RestoreAction, 
    UIFeedbackHooks 
} from "@/Shared";

export interface UseRestoreDomainOptions<T> {
    /** Domain label used in messages (e.g. "User", "Contact") */
    domainLabel : string,

    /** Whether the current user is allowed to restore */
    canRestore: boolean;

    restoreAction : RestoreAction<T>;

    /** UI feedback hooks (toast, confirm) */
    uiFeedbackHooks: UIFeedbackHooks;

    /** Callback executed after a successful restore */
    onSuccess?: () => void;
}


/**
 * useRestoreDomain hook
 *
 * Generic domain-level hook responsible for handling
 * restore workflows in a consistent and reusable way.
 *
 * It orchestrates:
 * - permission checks
 * - confirmation dialog
 * - async restore execution
 * - toast feedback (loading / success / error)
 *
 * This hook is UI-agnostic and domain-agnostic.
 * It relies only on injected callbacks and responses.
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export function useRestoreDomain<T>(options: UseRestoreDomainOptions<T>) {
    const {
        domainLabel,
        canRestore,
       restoreAction,
        uiFeedbackHooks,
        onSuccess,
    } = options;

    const { toastHook, confirmHook } = uiFeedbackHooks;
    const {execute, response, setResponse} = restoreAction;

    /**
     * Watches the restore async response and triggers
     * the appropriate UI feedback.
     */
    useEffect(() => {
        const { isSucceeded, isFailed } = ActionStatusHelper.is(
            response.status
        );

        if (isSucceeded) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.RESTORING.SUCCESS(domainLabel),
                    type: FeedBackType.SUCCESS,
                },
                onClose: () => {
                    ResponseHook.reset(setResponse)
                    onSuccess?.();
                },
            });
        }

        if (isFailed) {
            toastHook.openToast?.({
                data: {
                    message:
                        response.error ??
                        MessageHelper.RESTORING.ERROR(domainLabel),
                    type: FeedBackType.ERROR,
                },
                onClose: () => ResponseHook.reset(setResponse),
            });
        }
    }, [response.status, response.error]);

    /**
     * Executes the restore workflow:
     * 1. Permission check
     * 2. User confirmation
     * 3. Loading feedback
     * 4. Restore API call
     */
    const executeRestore = async (entityId : number | string) => {
        if (!canRestore) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.RESTORING.NOT_ALLOWED(domainLabel),
                    type: FeedBackType.WARNING,
                }
            });
            return;
        }

        const confirmed = await confirmHook.openConfirm?.({
            title: MessageHelper.MODAL.CONFIRM.TITLE,
            content: MessageHelper.MODAL.CONFIRM.RESTORING(domainLabel),
        });

        if (!confirmed || !execute || !entityId) {
            return;
        }

        toastHook.openToast?.({
            data: {
                message: MessageHelper.RESTORING.LOADING(domainLabel),
                type: FeedBackType.INFO,
                duration : LONG_PROCESS_TOAST_DURATION
            }
        });

        await execute(entityId);
    };

    /**
     * Public API
     */
    return {
        executeRestore,
    };
}